import { useTranslations } from "@zapier/i18n";

import {
  Button,
  Field,
  Link,
  ModalDialogBody,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogRoot,
  Text,
  TextInput,
} from "@zapier/design-system";

import { useAutoSelectInputText } from "@zapier/shared";
import styles from "./FolderRenameModal.module.css";
import { ChangeEvent, useState } from "react";
import { getMessageFromNetworkError, useRenameFolder } from "@zapier/client";
import { useNotification } from "@zapier/utils";

interface Props {
  id?: string;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  initialValue?: string;
}

interface ContentProps extends Props {
  onSubmit: (title: string) => void;
}

function FolderRenameModalContent({
  initialValue,
  isOpen,
  setIsOpen,
  onSubmit,
}: ContentProps) {
  const t = useTranslations("folders-rename-modal");
  const nameInputIdentifier = "rename-folders-name";

  const [value, setValue] = useState(initialValue || "");
  const [touched, setTouched] = useState(false);
  const isValid = value.length > 0 && value.length <= 255;
  const errorMessage =
    touched && value.length === 0
      ? t("errors.name-required")
      : value.length > 255
        ? t("errors.name-too-long")
        : undefined;

  useAutoSelectInputText({
    isOpen,
    inputSelector: `#${nameInputIdentifier}`,
    value: initialValue,
  });

  const submit = () => {
    setTouched(true);
    if (!isValid) {
      return;
    }
    if (value !== initialValue) {
      onSubmit(value);
    }
  };

  return (
    <ModalDialogRoot isOpen={true} onOpenChange={setIsOpen}>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- the form deliberately stops click/keydown propagation to keep the modal open and submit on Enter */}
      <form
        onSubmit={submit}
        onClick={(event) => event.stopPropagation()} // prevent the modal from closing when clicking inside the form
        onKeyDown={
          // if the enter key is being pressed submit the form
          (event) => {
            if (!isValid || value === initialValue) return;
            return event.key === "Enter" && submit();
          }
        }
      >
        <ModalDialogHeader textAlign="start">
          {t("headings.rename-folder", {
            initialValue: initialValue || "",
            hasName: initialValue ? "true" : "false",
          })}
        </ModalDialogHeader>
        <ModalDialogBody textAlign="start">
          <div className={styles.body}>
            <Field
              label={t("labels.name")}
              inputId={nameInputIdentifier}
              isRequired={true}
              error={errorMessage}
              renderInput={(inputProps) => (
                <TextInput
                  {...inputProps}
                  aria-describedby={inputProps.ariaDescribedBy}
                  // eslint-disable-next-line jsx-a11y/no-autofocus -- intentionally focus the input when the modal opens
                  autoFocus={true}
                  id={nameInputIdentifier}
                  defaultValue={initialValue}
                  isFullWidth={true}
                  maxLength={255}
                  onBlur={() => setTouched(true)}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    setTouched(true);
                    setValue(event.target?.value || "");
                  }}
                  placeholder={t("placeholders.untitled-folder")}
                />
              )}
            />
          </div>
        </ModalDialogBody>
        <ModalDialogFooter>
          <Button variant="secondary" onPress={() => setIsOpen(false)}>
            {t("buttons.cancel")}
          </Button>
          <Button
            isDisabled={!isValid || value === initialValue}
            onPress={submit}
            type="submit"
          >
            {t("buttons.save")}
          </Button>
        </ModalDialogFooter>
      </form>
    </ModalDialogRoot>
  );
}

export default function FolderRenameModal({
  setIsOpen,
  id,
  isOpen,
  initialValue = "",
}: Props) {
  const t = useTranslations("folders-rename-modal");

  const { createNotification } = useNotification();
  const { rename } = useRenameFolder({ id });

  /** This callback needs to happen outside of the modal so that it can
   * still display a notification when the request completes after the modal has closed.
   */
  const submit = (name: string) => {
    if (!id) {
      return;
    }

    rename(
      { name, folderId: id },
      {
        onSuccess: () => {
          createNotification({
            message: (
              <Text>
                {t.rich("messages.rename-success-with-undo", {
                  name,
                  undoLink: (chunks) => (
                    <Link onPress={() => undoSubmit(initialValue)}>
                      {chunks}
                    </Link>
                  ),
                })}
              </Text>
            ),
            type: "success",
          });
        },
        onError: (error: any) => {
          createNotification({
            title: t("errors.unable-to-rename", { name: initialValue }),
            message: getMessageFromNetworkError(error),
            type: "error",
          });
        },
      },
    );

    setIsOpen(false);
  };

  const undoSubmit = (name: string) => {
    if (!id) {
      return;
    }

    rename(
      { name, folderId: id },
      {
        onSuccess: () => {
          createNotification({
            message: <Text>{t("messages.name-restored", { name })}</Text>,
            type: "success",
          });
        },
        onError: (error: any) => {
          createNotification({
            title: t("errors.unable-to-restore"),
            message: getMessageFromNetworkError(error),
            type: "error",
          });
        },
      },
    );

    setIsOpen(false);
  };

  // Do not display the modal if isOpen==false
  if (!isOpen) {
    return;
  }

  return (
    <FolderRenameModalContent
      id={id}
      initialValue={initialValue}
      isOpen={isOpen}
      onSubmit={submit}
      setIsOpen={setIsOpen}
    />
  );
}
